// 幣別對照表與當前幣別狀態 — 移植 Unity `CurrencySettingSO` + `CurrencySetting.asset`。
//
// 後端於登入回應帶來幣別代碼（`ResponseInfo.currency`，如 "PHP"/"USD"），前端據此顯示對應
// 符號與顏色（Unity `SetCurrencyUI`/`GetCurrencyText`/`GetCurrencyColor`）。玩家端手動換幣
// （Unity `ChangeCurrency`）已停用（`CurrencySettingSO.GetCurrencyChangeSprite` 註解「目前沒換幣
// 功能，先隱藏」），故本模組只做「後端帶幣別 → 前端被動套用」的顯示，不提供 in-game 切換。
//
// 權威來源（逐筆抄錄，不捏造）：
//   slot026_redridinghood/Assets/SlotFramework/Data/CurrencySetting.asset（64 筆）
//   slot026_redridinghood/Assets/SlotFramework/Script/ScriptableObject/CurrencySettingSO.cs
//
// 符號一律以實際 Unicode 字元書寫（非 \uXXXX 跳脫），確保 `scripts/subset-fonts.mjs` 掃描字串
// 字面值時能收錄到正確碼位（否則泰銖 ฿、越南盾 ₫、亞美尼亞 ֏ 等會在子集字型缺字變豆腐）。
// Name 逐筆照抄 Unity（含其自身小瑕疵，如 Code=AED 卻標為 "Jordanian dinar"），僅供除錯／記錄。
//
// [已知未做] Unity 僅 COMBO（Code="COM"）以圖示 `pic_combo.png` 取代文字符號（`Sprite != null` →
// 顯圖藏字），其餘 63 筆皆文字符號。本移植的顯示端（BottomBarView/BetListPopup/BuyBonusDialog）
// 皆為文字 `Text` 節點、未載入 COMBO 圖示素材，故當前幣別為 COMBO 時會呈現「空符號＋粉色金額」
// （＝Unity 未載圖時的降級）。`usesImage` 旗標已保留供未來擴充。後端帶的是各國法幣代碼，文字
// 符號路徑已完整覆蓋實際情境。

/** 多數幣別的符號／金額色（Unity Color (250,240,180)）。 */
const COLOR_STD = 0xfaf0b4
/** COMBO 專用（Unity Color (255,230,255)）。 */
const COLOR_COMBO = 0xffe6ff
/** 6 個 100:1／1000:1 幣別的替代色（Unity Color (255,240,180)）。 */
const COLOR_ALT = 0xfff0b4

/** 單一幣別的顯示中繼資料（對應 Unity `Currency`）。 */
export interface CurrencyMeta {
  /** ISO 代碼（Unity `Currency.Code`；None 為空字串）。後端登入回應帶的即此代碼。 */
  readonly code: string
  /** 顯示名稱（Unity `Currency.Name`），僅供除錯／記錄，不用於顯示。 */
  readonly name: string
  /** 貨幣符號文字（Unity `Currency.Symbol`；COMBO 與部分 100:1 幣別為空字串）。 */
  readonly symbol: string
  /** 符號與金額文字顏色（Unity `Currency.Color`，0xRRGGBB）。 */
  readonly color: number
  /** Unity `Currency.Sprite != null`（顯圖藏字）；目前僅 COMBO=true，見檔頭「已知未做」。 */
  readonly usesImage: boolean
}

/** Code="" 的 None 條目（Unity `GetDefaultCurrency`）：查無代碼時的 fallback。 */
const NONE: CurrencyMeta = { code: '', name: 'None', symbol: '', color: COLOR_STD, usesImage: false }

/**
 * 逐筆抄錄自 `CurrencySetting.asset`（保留 Unity 原始順序）。第一筆為 None fallback，
 * 第二筆 COMBO 是唯一 `usesImage`（有 `pic_combo.png`）者。
 */
const CURRENCY_LIST: readonly CurrencyMeta[] = [
  NONE,
  { code: 'COM', name: 'COMBO', symbol: '', color: COLOR_COMBO, usesImage: true },
  { code: 'PHP', name: 'Philippine peso', symbol: '₱', color: COLOR_STD, usesImage: false },
  { code: 'USD', name: 'United States dollar', symbol: '$', color: COLOR_STD, usesImage: false },
  { code: 'TWD', name: 'New Taiwan dollar', symbol: '$', color: COLOR_STD, usesImage: false },
  { code: 'TWDp', name: 'New Taiwan dollar(100:1)', symbol: '', color: COLOR_ALT, usesImage: false },
  { code: 'TWDv', name: 'New Taiwan dollar(130:1)', symbol: '', color: COLOR_ALT, usesImage: false },
  { code: 'BDT', name: 'Bangladeshi taka', symbol: '৳', color: COLOR_STD, usesImage: false },
  { code: 'BND', name: 'Brunei dollar', symbol: '$', color: COLOR_STD, usesImage: false },
  { code: 'BRL', name: 'Brazilian real', symbol: 'R$', color: COLOR_STD, usesImage: false },
  { code: 'CNY', name: 'Renminbi', symbol: '¥', color: COLOR_STD, usesImage: false },
  { code: 'EUR', name: 'Euro', symbol: '€', color: COLOR_STD, usesImage: false },
  { code: 'HKD', name: 'Hong Kong dollar', symbol: '$', color: COLOR_STD, usesImage: false },
  { code: 'INR', name: 'Indian rupee', symbol: '₹', color: COLOR_STD, usesImage: false },
  { code: 'JPY', name: 'Japanese yen', symbol: '¥', color: COLOR_STD, usesImage: false },
  { code: 'KRW', name: 'South Korean won', symbol: '₩', color: COLOR_STD, usesImage: false },
  { code: 'LKR', name: 'Sri Lankan rupee', symbol: 'Rs', color: COLOR_STD, usesImage: false },
  { code: 'MMK', name: 'Burmese kyat', symbol: 'K', color: COLOR_STD, usesImage: false },
  { code: 'kMMK', name: 'kMMK', symbol: 'MMK(K)', color: COLOR_STD, usesImage: false },
  { code: 'MXN', name: 'Mexican peso', symbol: '$', color: COLOR_STD, usesImage: false },
  { code: 'MYR', name: 'Malaysian ringgit', symbol: 'RM', color: COLOR_STD, usesImage: false },
  { code: 'MYRh', name: 'Malaysian ringgit(100:1)', symbol: 'hRM', color: COLOR_ALT, usesImage: false },
  { code: 'MYRd', name: 'Malaysian ringgit(1000:1)', symbol: 'kRM', color: COLOR_ALT, usesImage: false },
  { code: 'PKR', name: 'Pakistani rupee', symbol: '₨', color: COLOR_STD, usesImage: false },
  { code: 'IDR', name: 'Indonesian rupiah', symbol: 'Rp', color: COLOR_STD, usesImage: false },
  { code: 'RUB', name: 'Russian ruble', symbol: '₽', color: COLOR_STD, usesImage: false },
  { code: 'SGD', name: 'Singapore dollar', symbol: '$', color: COLOR_STD, usesImage: false },
  { code: 'THB', name: 'Thai baht', symbol: '฿', color: COLOR_STD, usesImage: false },
  { code: 'VND', name: 'Vietnamese đồng', symbol: '₫', color: COLOR_STD, usesImage: false },
  { code: 'kVND', name: 'kVND', symbol: 'VND(K)', color: COLOR_STD, usesImage: false },
  { code: 'kKHR', name: 'kKHR', symbol: 'KHR(k)', color: COLOR_STD, usesImage: false },
  { code: 'LAK', name: 'Lao kip', symbol: '₭', color: COLOR_ALT, usesImage: false },
  { code: 'kLAK', name: 'kLAK', symbol: 'LAK(k)', color: COLOR_STD, usesImage: false },
  { code: 'kKRW', name: 'kKRW', symbol: 'KRW(K)', color: COLOR_STD, usesImage: false },
  { code: 'kIDR', name: 'kIDR', symbol: 'IDR(K)', color: COLOR_STD, usesImage: false },
  { code: 'AED', name: 'Jordanian dinar', symbol: 'إ.د', color: COLOR_STD, usesImage: false },
  { code: 'NPR', name: 'Nepalese rupee', symbol: '₨', color: COLOR_STD, usesImage: false },
  { code: 'GBP', name: 'Pound sterling', symbol: '£', color: COLOR_STD, usesImage: false },
  { code: 'AMD', name: 'Armenian dram', symbol: '֏', color: COLOR_STD, usesImage: false },
  { code: 'AUD', name: 'Australian dollar', symbol: 'A$', color: COLOR_STD, usesImage: false },
  { code: 'ARS', name: 'Argentine peso', symbol: '$', color: COLOR_STD, usesImage: false },
  { code: 'CAD', name: 'Canadian dollar', symbol: 'C$', color: COLOR_STD, usesImage: false },
  { code: 'CHF', name: 'Swiss franc', symbol: 'SFr.', color: COLOR_STD, usesImage: false },
  { code: 'CLP', name: 'Chilean peso', symbol: '$', color: COLOR_STD, usesImage: false },
  { code: 'COP', name: 'Colombian peso', symbol: '$', color: COLOR_STD, usesImage: false },
  { code: 'GEL', name: 'Georgian lari', symbol: '₾', color: COLOR_STD, usesImage: false },
  { code: 'GHS', name: 'Ghanaian cedi', symbol: 'GH¢', color: COLOR_STD, usesImage: false },
  { code: 'KES', name: 'Kenyan shilling', symbol: 'Ksh', color: COLOR_STD, usesImage: false },
  { code: 'KZT', name: 'Kazakhstani tenge', symbol: '₸', color: COLOR_STD, usesImage: false },
  { code: 'NGN', name: 'Nigerian naira', symbol: '₦', color: COLOR_STD, usesImage: false },
  { code: 'NOK', name: 'Norwegian krone', symbol: 'kr', color: COLOR_STD, usesImage: false },
  { code: 'NZD', name: 'New Zealand dollar', symbol: '$', color: COLOR_STD, usesImage: false },
  { code: 'OMR', name: 'Omani rial', symbol: 'ع.ر.', color: COLOR_STD, usesImage: false },
  { code: 'PGK', name: 'Papua New Guinean kina', symbol: 'K', color: COLOR_STD, usesImage: false },
  { code: 'PEN', name: 'Peruvian sol', symbol: 'S/.', color: COLOR_STD, usesImage: false },
  { code: 'PLN', name: 'Polish złoty', symbol: 'zł', color: COLOR_STD, usesImage: false },
  { code: 'SAR', name: 'Saudi riyal', symbol: 'SR', color: COLOR_STD, usesImage: false },
  { code: 'SEK', name: 'Swedish krona', symbol: 'kr', color: COLOR_STD, usesImage: false },
  { code: 'TND', name: 'Tunisian dinar', symbol: 'DT', color: COLOR_STD, usesImage: false },
  { code: 'TRY', name: 'Turkish lira', symbol: 'TL', color: COLOR_STD, usesImage: false },
  { code: 'TZS', name: 'Tanzanian shilling', symbol: 'tzs', color: COLOR_STD, usesImage: false },
  { code: 'VES', name: 'Venezuelan bolívar', symbol: 'Bs.', color: COLOR_STD, usesImage: false },
  { code: 'ZAR', name: 'South African rand', symbol: 'R', color: COLOR_STD, usesImage: false },
  { code: 'UGX', name: 'Ugandan shilling', symbol: 'Sh', color: COLOR_ALT, usesImage: false },
]

const CURRENCY_BY_CODE = new Map<string, CurrencyMeta>(CURRENCY_LIST.map((c) => [c.code, c]))

/** None 條目（Unity `GetDefaultCurrency`）：查無代碼時的 fallback。 */
export const NONE_CURRENCY: CurrencyMeta = NONE

/**
 * 依後端幣別代碼取對照（對應 Unity `CurrencySettingSO.GetCurrency`）：查到即回、查無回 None。
 *
 * 註：Unity 另有 `UtilityConstants.TryParseCurrency`（字串 → `ECurrency`，enum 解析失敗 fallback
 * 為 PHP）的中介層；本移植以字串直接查表對應 `GetCurrency`（查無 → None ＝無符號＋標準金色的
 * 安全降級，不會把未知代碼誤顯示成某國符號誤導玩家）。後端帶的是表內合法代碼，查無實務上不會發生。
 */
export function resolveCurrency(code: string): CurrencyMeta {
  return CURRENCY_BY_CODE.get(code.trim()) ?? NONE_CURRENCY
}

/** 當前幣別；預設 PHP（沿用先前寫死 ₱ 的行為，接線前不致空白）。開機由 main.ts 以後端值覆寫。 */
let current: CurrencyMeta = resolveCurrency('PHP')

/**
 * 設定當前幣別（開機時 main.ts 以後端 `client.currency` 呼叫，離線 Mock 用 `.env` 的 VITE_CURRENCY）。
 * 對應 Unity 收到登入回應後把幣別存入 `BaseGameService` 供各 UI 的 `SetCurrencyUI` 取用。
 */
export function setCurrentCurrency(code: string): void {
  current = resolveCurrency(code)
}

/** 當前幣別中繼資料。 */
export function getCurrentCurrency(): CurrencyMeta {
  return current
}

/** 當前幣別符號（對應 Unity `GetCurrencyText`）。 */
export function getCurrencySymbol(): string {
  return current.symbol
}

/** 當前幣別的符號／金額色 0xRRGGBB（對應 Unity `GetCurrencyColor`）。 */
export function getCurrencyColor(): number {
  return current.color
}
